/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

/**
 * Current open issues:
 * How do  we decide if the role is editable and how do we make the
 * UI repsond appropriatly (i.e. display the dropdown or the grayed
 * out thing)?
 *
 * Inputs:
 *   list of roles
 *   currently selected role
 * Outputs:
 *   what happens on selection change...?
 */
import {
	Component,
	Input,
	ChangeDetectionStrategy,
	Self,
	Optional,
	EventEmitter,
	Output,
	ChangeDetectorRef,
	OnInit,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { TicketEditBaseComponent } from '../ticket-edit-base/ticket-edit-base.component';
import { MultiWrapperComponent } from '../multi-wrapper/multi-wrapper.component';
import { ProjectMember } from '@touchplan/common';
import { User } from '@touchplan/common';

interface ISelectItem {
	label: string;
	value: string;
}

/**
 * Displays the ticket's role.  If the role is editable, then it's
 * displayed as a dropdown.  If it's not editable, it's displayed
 * in a grayed out panel.
 *
 * More detail...
 */
@Component({
	selector: 'ticket-project-member',
	templateUrl: './ticket-project-member.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrls: ['./ticket-project-member.component.scss'],
})
export class TicketProjectMemberComponent extends TicketEditBaseComponent implements OnInit {
	/**
	 * @input roles - The list of roles to choose from
	 */
	@Input() public projectMemberList$: Subject<Map<string, ProjectMember>>;
	@Input() public projectMember: ProjectMember;
	@Input() public placeholderText = 'Select a Person';
	@Input() public allowNullSelection = true;
	@Input() public sortAlphabetically = true;
	@Output() public changed: EventEmitter<any> = new EventEmitter();
	@Output() public selected: EventEmitter<string> = new EventEmitter();

	public internalListSubject$: BehaviorSubject<Map<string, ISelectItem>> = new BehaviorSubject(new Map());

	constructor(
		@Self() @Optional() public control: NgControl,
		@Optional() public multiWrapper: MultiWrapperComponent,
		@Optional() protected cd: ChangeDetectorRef
	) {
		super(control, multiWrapper, cd);
	}

	public ngOnInit() {
		super.ngOnInit();

		this.internalField.valueChanges.pipe(takeUntil(this.shutdownSubject)).subscribe((val: number) => {
			this.onChange(val);
			this.changed.emit(val);
			this.selected.emit();
		});

		this.projectMemberList$
			.pipe(
				takeUntil(this.shutdownSubject),
				map((list: Map<string, ProjectMember>) => {
					// First, alphabetically sort the project members if option is enabled
					const sortedProjectMembers = this.sortAlphabetically
						? new Map(
								[...list.entries()].sort((a, b) => {
									const firstName: string =
										a[1].view.user && a[1].view.user.firstName && a[1].view.user.lastName
											? a[1].view.user.firstName.toLowerCase() + a[1].view.user.lastName.toLowerCase()
											: 'N/A';
									const secondName: string =
										b[1].view.user && b[1].view.user.firstName && b[1].view.user.lastName
											? b[1].view.user.firstName.toLowerCase() + b[1].view.user.lastName.toLowerCase()
											: 'N/A';
									return firstName.localeCompare(secondName);
								})
						  )
						: list;

					const internalList = new Map();
					if (this.allowNullSelection) {
						internalList.set(this.placeholderText, { label: this.placeholderText, value: null });
					}
					sortedProjectMembers.forEach((otherProjectMember: ProjectMember, projectMemberId: string) => {
						// Filter to only project members we have access to
						if (this.projectMember && !this.filterWithSameRole(this.projectMember, otherProjectMember)) {
							return false;
						}
						const user: User = otherProjectMember.view.user;
						if (user) {
							const label = user.firstName + ' ' + user.lastName;
							internalList.set(label, {
								label,
								value: projectMemberId,
							});
						}
					});
					return internalList;
				})
			)
			.subscribe(this.internalListSubject$);
	}

	// Returns true if other project membeber is admin OR has access to at least one of the same roles as you
	private filterWithSameRole(you: ProjectMember, other: ProjectMember): boolean {
		// If you are an admin, you have access to all
		if (you.view.accessLevel === 'Admin' || other.view.accessLevel === 'Admin') {
			return true;
		}
		const youPrimaryRoleId: string = you.view.roleId;
		const yourSecondaryRoles = you.view.secondaryRoles
			? you.view.secondaryRoles
			: Object.assign({}, { [youPrimaryRoleId]: youPrimaryRoleId });
		const otherPrimaryRoleId: string = other.view.roleId;
		const otherSecondaryRoles = other.view.secondaryRoles
			? other.view.secondaryRoles
			: Object.assign({}, { [otherPrimaryRoleId]: otherPrimaryRoleId });

		// Iterate the source project member secondary roles
		// Then for each secondary role from the other project member, iterate that too.
		// We only need one match per project member
		for (const youRoleId in yourSecondaryRoles) {
			if (yourSecondaryRoles.hasOwnProperty(youRoleId)) {
				for (const otherRoleId in otherSecondaryRoles) {
					if (otherRoleId === youRoleId) {
						return true;
					}
				}
			}
		}

		return false;
	}
}
